import { Col } from "antd";
import Title from "antd/es/typography/Title";
import { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { capitalize } from "@src/utils/capitalize";
import { formatDateToTimeOnly } from "@src/utils/dateHelpers";

import {
  DataPoint,
  CHART_DATA_UPDATE_INTERVAL,
  updateDataPoints,
  createEmptyDataPoint,
  CHART_INTERPOLATION_TIME,
} from "./utils";

interface SensorGraphProps {
  name: string;
  latestData: { timestamp: string; value: number } | null;
  color: string;
}

const SensorGraph = ({ name, latestData, color }: SensorGraphProps) => {
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const [maxValue, setMaxValue] = useState<number>(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDataPoints((currentDataPoints) => {
        const currentTime = new Date().getTime();
        let newDataPoint: DataPoint;

        if (
          latestData &&
          new Date(latestData.timestamp).getTime() >
            currentTime - CHART_INTERPOLATION_TIME
        ) {
          const newValue = Number(latestData.value.toFixed(2));
          newDataPoint = {
            time: new Date(latestData.timestamp),
            value: newValue,
          };
          setMaxValue((prevMax) => Math.max(prevMax, newValue));
        } else {
          newDataPoint = createEmptyDataPoint();
        }

        return updateDataPoints(currentDataPoints, newDataPoint);
      });
    }, CHART_DATA_UPDATE_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [latestData]);

  const yAxisDomain = useMemo(() => {
    if (maxValue === 0) {
      return [0, 1];
    }
    return [0, Math.ceil(maxValue * 1.1)];
  }, [maxValue]);

  return (
    <Col span={12}>
      <Title level={5}>{`Sensor Data ${capitalize(name)}`}</Title>
      <ResponsiveContainer
        width="100%"
        height={250}
        style={{ marginRight: "20px" }}
      >
        <LineChart data={dataPoints}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tickFormatter={(time: Date) =>
              formatDateToTimeOnly(time.toISOString())
            }
          />
          <YAxis domain={yAxisDomain} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Col>
  );
};

export default SensorGraph;
